import React, { useState, useEffect } from 'react';
import { getActionErrorMessage } from '../utils/errorHandler';
import '../styles/PreceptorEvaluationForm.css'; // We can reuse the same styles
import "../styles/theme.css";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StudentEvaluationForm = () => {
  const [selectedPreceptor, setSelectedPreceptor] = useState('');
  const [evaluation, setEvaluation] = useState({
    degree: 0,
    description: '',
    comment: ''
  });
  const [preceptors, setPreceptors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [evaluationPeriods, setEvaluationPeriods] = useState([]);
  const [currentRotation, setCurrentRotation] = useState(null);
  const [isEvaluationPeriod, setIsEvaluationPeriod] = useState(false);

  useEffect(() => {
    const fetchEvaluationPeriods = async () => {
      try {
        const currentYear = new Date().getMonth() >= 7 ? 
        new Date().getFullYear() : 
        new Date().getFullYear() - 1;

        const response = await fetch(getApiUrl(`config/get_student_evaluation_dates/${currentYear}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch evaluation periods');
        }

        const data = await response.json();
        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Evaluation periods data:', data);
        
        // Extract the evaluation periods array
        const periods = data.student_to_preceptor_eval || [];
        setEvaluationPeriods(periods);
        
        // Check if current date falls within any evaluation period
        const now = new Date();
        
        let foundActivePeriod = false;
        for (let i = 0; i < periods.length; i++) {
          const start = new Date(periods[i].startDate);
          const end = new Date(periods[i].endDate);
          
          if (now >= start && now <= end) {
            setIsEvaluationPeriod(true);
            setCurrentRotation(i);
            foundActivePeriod = true;
            break;
          }
        }

        if (!foundActivePeriod) {
          setIsEvaluationPeriod(false);
          setCurrentRotation(null);
        }

      } catch (err) {
        console.error('Error in fetchEvaluationPeriods:', err);
        setError('Failed to fetch evaluation periods');
        setEvaluationPeriods([]); // Ensure periods are cleared on error
      }
    };

    fetchEvaluationPeriods();
  }, []);

  useEffect(() => {
    const fetchPreceptors = async () => {
      if (!isEvaluationPeriod || currentRotation === null) return;

      try {
        const currentDate = new Date();
        const curriculumYear = currentDate.getMonth() >= 7 ? 
          currentDate.getFullYear() : 
          currentDate.getFullYear() - 1;

        const response = await axiosInstance.get(
          getApiUrl(`crud/get_preceptor_by_student_rotation/${currentRotation}?year=${curriculumYear}`),
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch preceptors');
        }

        const data = await response.json();
        
        if (Array.isArray(data)) {
          setPreceptors(data);
        } else if (data && typeof data === 'object') {
          setPreceptors([data]); // Convert single object to array
        } else {
          setPreceptors([]);
          setError('Invalid preceptor data received');
        }
      } catch (err) {
        console.error('Failed to fetch preceptors:', err);
        setPreceptors([]);
        setError('Failed to fetch preceptors');
      }
    };

    fetchPreceptors();
  }, [isEvaluationPeriod, currentRotation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
              const response = await fetch(getApiUrl('crud/send_student_to_preceptor_evaluation/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preceptor_id: selectedPreceptor,
          eval_degree: evaluation.degree,
          eval_description: evaluation.description,
          eval_comment: evaluation.comment
        })
      });

      if (!response.ok) throw new Error('Failed to submit evaluation');
      
      setSuccess('Evaluation submitted successfully!');
      setSelectedPreceptor('');
      setEvaluation({
        degree: 0,
        description: '',
        comment: ''
      });
    } catch (err) {
      setError(getActionErrorMessage(err, 'load evaluation form'));
    } finally {
      setLoading(false);
    }
  };

  const renderEvaluationSchedule = () => {
    return (
      <div className="evaluation-schedule">
        <h2>Evaluation Periods Schedule</h2>
        <div className="schedule-list">
          {evaluationPeriods.map((period, index) => (
            <div key={index} className="schedule-item">
              <h3>Rotation {index + 1}</h3>
              <p>Start: {formatDate(period.startDate)}</p>
              <p>End: {formatDate(period.endDate)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="content-box">
        <div className="modern-schedule-header">
          <div>
            <h1 className="page-title">Preceptor Evaluation</h1>
            <p className="schedule-subtitle">Evaluate your preceptor's performance</p>
          </div>
        </div>
        <div className="modern-form-container">
          <div className="evaluation-card">
            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}
            
            {!isEvaluationPeriod ? (
              <div className="closed-form-container">
                <div className="alert info">
                  Evaluation form is currently closed. Evaluations can only be submitted during designated evaluation periods.
                </div>
                {renderEvaluationSchedule()}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="evaluation-form">
                <div className="form-group">
                  <label>Select Preceptor</label>
                  <select
                    value={selectedPreceptor}
                    onChange={(e) => setSelectedPreceptor(e.target.value)}
                    required
                  >
                    <option value="">Choose a preceptor</option>
                    {Array.isArray(preceptors) && preceptors.map((preceptor) => (
                      <option key={preceptor.id} value={preceptor.id}>
                        {preceptor.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Evaluation Score (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={evaluation.degree}
                    onChange={(e) => setEvaluation({
                      ...evaluation,
                      degree: parseFloat(e.target.value)
                    })}
                    required
                    placeholder="Enter score"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={evaluation.description}
                    onChange={(e) => setEvaluation({
                      ...evaluation,
                      description: e.target.value
                    })}
                    required
                    placeholder="Brief description of the evaluation"
                  />
                </div>

                <div className="form-group">
                  <label>Comments</label>
                  <textarea
                    value={evaluation.comment}
                    onChange={(e) => setEvaluation({
                      ...evaluation,
                      comment: e.target.value
                    })}
                    rows="4"
                    required
                    placeholder="Detailed comments about the preceptor's performance"
                  />
                </div>

                <button
                  type="submit"
                  className={`submit-button ${loading ? 'loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Evaluation'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentEvaluationForm;